import * as React from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { DataGrid } from '@mui/x-data-grid/DataGrid';
import Paper from "@mui/material/Paper";
import {
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useEffect, useState } from "react";
import { ADMIN_USERS, ADMIN_ORGANIZATIONS } from "../../../Services/Endpoint";
import { User } from "../../../Shared-ui/Types/User";
import { StartLoading, StopLoading } from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { useDispatch } from "react-redux";
import { ShowError, ShowSuccess } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import ConfirmPopup from "../../../Shared-ui/SharedComponents/ConfirmPopup";
import ConfirmBase from "../../../Shared-ui/Types/Confirm";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { Organization } from "../../../Shared-ui/Types/Admin";



interface MyComponentProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  setUser: React.Dispatch<React.SetStateAction<User | null>>
  open: boolean
}



const UsersData: React.FC<MyComponentProps> = (props) => {
  const dispatch = useDispatch();
  const [confirmPopupData, setConfirmPopupData] = useState<ConfirmBase>({ show: false })
  const [users, setUsers] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [editingIndex, setEditingIndex] = useState<any>(null)
  const [totalPage, setTotalPage] = useState(0)
  const [limit, setLimit] = useState(999)
  const [organizationNameMap, setOrganizationNameMap] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const data = await BaseInstance.get(ADMIN_ORGANIZATIONS).then(res => res?.data);
      const orgMap: Record<string, string> = {};
      data.forEach((org: Organization) => {
        if (org.id && org.name) {
          orgMap[org.id] = org.name;
        }
      });

      setOrganizationNameMap(orgMap);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail || "Failed to load organizations"));
    }
  };


  useEffect(() => {
    if (!props.open) {
      getUsers()
    }
  }, [props.open])

  useEffect(() => {
    getUsers()
  }, [])

  const getUsers = async () => {
    try {
      dispatch(StartLoading())
      const data = await BaseInstance.get(`${ADMIN_USERS}?skip=${0}&limit=${limit}`).then(res => res?.data)
      data.sort((a: { full_name: string }, b: { full_name: string }) => a.full_name.localeCompare(b.full_name));
      setUsers(data)
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  }

  const deleteUser = async (rowInfo: any) => {
    try {
      setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
      dispatch(StartLoading())
      const user: User = rowInfo.row
      const data = await BaseInstance.delete(`${ADMIN_USERS}/${user.id}`).then(res => res?.data)
      dispatch(ShowSuccess("User Deleted"))
      getUsers()
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  }



  // ///////////////// columns
  const commonColumns: GridColDef[] = [
    { field: "full_name", headerName: "Name", width: 300 },
    { field: "email", headerName: "Email", width: 300, },
    { field: "is_superuser", headerName: "Super User", width: 150, align: "center", headerAlign: "center", renderCell: (params) => (params.value ? "Yes" : "No"), },
    { field: "organization_id", headerName: "Organization", flex: 0.8, minWidth: 150, align: "center", headerAlign: "center", renderCell: (params) => organizationNameMap[params.value] || params.value, },
    { field: "subscription", headerName: "Subscription", width: 150, align: "center", headerAlign: "center", },
    { field: "role", headerName: "Role", width: 120, align: "center", headerAlign: "center", },
    {
      field: "is_active", headerName: "Status", width: 100, align: "center", headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ color: params.value ? "green" : "red" }}>
          {params.value ? "Active" : "Inactive"}
        </Box>
      ),


    }
  ]


  // { field: "superuser", headerName: "Superuser", width: 280 },

  const actionColumns: GridColDef[] = [

    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const handleEditClick = (event: React.MouseEvent) => {
          event.stopPropagation(); // Prevent row selection
          props.setUser(params.row)
          props.setOpen(true)
        };

        const handleDeleteClick = (event: React.MouseEvent) => {
          event.stopPropagation(); // Prevent row selection
          setConfirmPopupData({
            show: true,
            setConfirmPopupData: setConfirmPopupData,
            title: "Confirm Deletion",
            info: "Are you sure want to delete your data? This action cannot be undone.",
            varient: "delete",
            functionName: deleteUser,
            functionParam: params
          })
        };



        return (
          <>
            <Box>
              <Tooltip title="Edit" placement="top-start">
                <IconButton aria-label="edit" onClick={handleEditClick}
                  disabled={Boolean((editingIndex !== params.id && editingIndex))}
                >
                  <EditOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" placement="top-start">
                <IconButton aria-label="delete" onClick={handleDeleteClick}
                  disabled={Boolean((editingIndex !== params.id && editingIndex))}
                >
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Box>


          </>
        );
      },
    },

  ];

  const modifiedColumn = (): GridColDef[] => {
    const columns: GridColDef[] = [
      ...commonColumns, ...actionColumns
    ]
    return columns

  }


  return (
    <>
      <Box>
        {/* <Typography variant="h6">All Users</Typography> */}
        <Paper variant="outlined" sx={{ height: "100%", width: "100%" }}>
          <DataGrid
            rows={users}
            columns={modifiedColumn()}
            pageSizeOptions={[10]}
            paginationMode="client"
            disableColumnResize // Disables resizing

            sx={{
              border: 0,
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
            }}
          />
        </Paper>
      </Box>
      {/* pop up dialog */}
      <ConfirmPopup details={confirmPopupData}
      />
    </>
  );
};

export default UsersData;
